import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm as useReactHookForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import { useAuthStore } from '@/core/stores/auth.store';
import { useForm } from '../hooks/useForm';
import { useSubmitForm } from '../hooks/useSubmitForm';
import { FormResponseView } from '../components/FormRenderer/FormResponseView';
import { buildSubmissionSchema, mapSubmissionValues } from '../schemas';

/**
 * Respond-to-form view for authenticated org members, using the authenticated
 * `GET /forms/{id}`. Distinct from `PublicFormSubmissionPage` (`/f/:formId`,
 * `audienceType: Public`, no auth) and `FormAccessByTokenPage`
 * (`/f/access/:token`, `Specific`-recipient links).
 */
export default function FormResponsePage() {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const { orgId, formId } = useParams<{ orgId: string; formId: string }>();
  const userEmail = useAuthStore((s) => s.user?.email);
  const { form, isLoading, isError } = useForm(formId ?? '');
  const submitForm = useSubmitForm(formId ?? '');
  const [submitted, setSubmitted] = useState(false);

  const sortedFields = form?.fields.slice().sort((a, b) => a.order - b.order) ?? [];
  // The backend's emailRequired check only applies to submitters with no attributed account
  // (public/share-token); an authenticated org member's identity is already known.
  const schema = buildSubmissionSchema(sortedFields, false);

  const rhfForm = useReactHookForm({
    resolver: zodResolver(schema),
    defaultValues: { email: userEmail ?? '', fields: {} },
  });

  const handleSubmit = rhfForm.handleSubmit(async (data) => {
    try {
      await submitForm.mutateAsync({
        email: data.email || userEmail || null,
        isAnonymous: false,
        values: mapSubmissionValues(data.fields as Record<string, unknown>),
      });
      setSubmitted(true);
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  });

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <FormResponseView
        testIdPrefix="forms-response"
        isLoading={isLoading}
        isError={isError}
        form={form}
        submitted={submitted}
        rhfForm={rhfForm}
        onSubmit={handleSubmit}
        isSubmitting={submitForm.isPending}
        backAction={{
          label: t('list.title'),
          onClick: () => navigate(`/${orgId}/forms`),
        }}
      />
    </FeatureErrorBoundary>
  );
}
